import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://guide.calmastays.com';
const SITE_NAME = 'Calma Guide Tenerife';
const DEFAULT_IMAGE = 'https://guide.calmastays.com/og-image.jpg';

const SEO = ({ 
  title, 
  description, 
  path = '/', 
  image = DEFAULT_IMAGE, 
  type = 'website',
  schema,
  lang = 'es',
  noindex = false 
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const url = `${SITE_URL}${path}`;
  const absImage = image && image.startsWith('/') ? `${SITE_URL}${image}` : image;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={absImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang === 'de' ? 'de_DE' : lang === 'en' ? 'en_GB' : 'es_ES'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />

      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* JSON-LD Schema */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

// Schema generators
export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": SITE_NAME,
  "url": SITE_URL,
  "description": "La guia turistica mas completa de Tenerife. Lugares, senderos, eventos, tiempo y webcams en vivo. The most complete Tenerife tourism guide.",
  "inLanguage": ["es", "en", "de"],
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${SITE_URL}/trails?search={search_term_string}`,
    "query-input": "required name=search_term_string"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Calme Stays",
    "url": "https://calmastays.com"
  }
});

export const trailSchema = (trail, lang) => ({
  "@context": "https://schema.org",
  "@type": "TouristTrip",
  "name": trail.name,
  "description": trail.tips?.[0] || `Sendero ${trail.name} en Tenerife`,
  "touristType": trail.trail_class === "Sendero" ? "Hiking" : trail.trail_class,
  "url": `${SITE_URL}/trails/${trail.id}`,
  "itinerary": {
    "@type": "Place",
    "name": trail.name,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": trail.municipalities?.[0] || "Tenerife",
      "addressRegion": "Santa Cruz de Tenerife",
      "addressCountry": "ES"
    },
    ...(trail.start_latitude && {
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": trail.start_latitude,
        "longitude": trail.start_longitude
      }
    })
  },
  "additionalProperty": [
    { "@type": "PropertyValue", "name": "distance", "value": `${(trail.distance_meters / 1000).toFixed(1)} km` },
    { "@type": "PropertyValue", "name": "difficulty", "value": trail.difficulty },
    { "@type": "PropertyValue", "name": "elevationGain", "value": `+${Math.round(trail.elevation_gain)}m` },
    ...(trail.duration_hours ? [{ "@type": "PropertyValue", "name": "duration", "value": `${trail.duration_hours} hours` }] : [])
  ]
});

export const eventSchema = (event) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  "name": event.title_es || event.title,
  "description": event.description_es || event.description,
  "startDate": event.start_date,
  ...(event.end_date && { "endDate": event.end_date }),
  "location": {
    "@type": "Place",
    "name": event.venue || "Tenerife",
    "address": {
      "@type": "PostalAddress",
      "addressRegion": "Santa Cruz de Tenerife",
      "addressCountry": "ES"
    }
  },
  "url": `${SITE_URL}/events`
});

export const placeSchema = (place) => ({
  "@context": "https://schema.org",
  "@type": "TouristAttraction",
  "name": place.name_es || place.name,
  "description": place.description_es || place.description,
  "url": `${SITE_URL}/place/${place.id}`,
  ...(place.latitude && {
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": place.latitude,
      "longitude": place.longitude
    }
  }),
  ...(place.google_rating && {
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": place.google_rating,
      "ratingCount": place.google_reviews_count || 1
    }
  }),
  "address": {
    "@type": "PostalAddress",
    "addressRegion": "Santa Cruz de Tenerife",
    "addressCountry": "ES"
  }
});

export const articleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title_es || article.title,
  "description": article.summary_es || article.summary,
  "url": `${SITE_URL}/article/${article.id}`,
  "inLanguage": "es",
  "publisher": {
    "@type": "Organization",
    "name": SITE_NAME
  }
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.a
    }
  }))
});

export default SEO;
